import React, { useEffect, useMemo, useRef, useState } from 'react';
import './InputSearchs.css';
import { Link, useNavigate } from 'react-router-dom';
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import { faArrowLeft, faSearch } from '@fortawesome/free-solid-svg-icons';
import Modal from 'react-modal';
import { useCatalogData } from '../CatalogData/CatalogDataProvider';
import {
    cleanCatalogText,
    createCatalogCategoryNameMap,
    filterCatalogProducts,
    normalizeCatalogId,
    normalizeCatalogSearchText,
} from '../../utils/catalogSearch';

const noop = () => {};
const RESULTS_PAGE_SIZE = 5;

const createRankedSections = (rankedProducts, categoryNames) => {
    return rankedProducts.reduce((sections, product) => {
        const categoryId = normalizeCatalogId(product?.idCategoria);
        const categoryName = cleanCatalogText(categoryNames.get(categoryId))
            || 'Productos';
        const categoryKey = categoryNames.has(categoryId)
            ? categoryId
            : 'uncategorized';
        const previousSection = sections[sections.length - 1];

        if (previousSection?.categoryKey === categoryKey) {
            previousSection.productos.push(product);
            return sections;
        }

        sections.push({
            categoryKey,
            categoryName,
            productos: [product],
        });
        return sections;
    }, []);
};

export default function InputSearchs({
    isOpen = false,
    onAfterClose = noop,
    onRequestClose = noop,
}) {
    const [searchTerm, setSearchTerm] = useState('');
    const [visibleProductCount, setVisibleProductCount] = useState(RESULTS_PAGE_SIZE);
    const inputRef = useRef(null);
    const openCycleStartedRef = useRef(false);
    const navigate = useNavigate();
    const {
        productos,
        categorias,
        status,
        errors,
        ensureProductos,
        ensureCategorias,
        retryProductos,
        retryCategorias,
    } = useCatalogData();
    const hasCatalogError = Boolean(errors.productos || errors.categorias)
        || status.productos === 'error'
        || status.categorias === 'error';
    const catalogLoadState = hasCatalogError
        ? 'error'
        : status.productos === 'loaded' && status.categorias === 'loaded'
            ? 'loaded'
            : status.productos === 'loading' || status.categorias === 'loading'
                ? 'loading'
                : 'idle';
    const normalizedSearchTerm = normalizeCatalogSearchText(searchTerm);

    useEffect(() => {
        if (!isOpen) {
            openCycleStartedRef.current = false;
            return;
        }

        if (openCycleStartedRef.current) {
            return;
        }

        openCycleStartedRef.current = true;

        const productsRequest = status.productos === 'error'
            ? retryProductos()
            : status.productos === 'idle'
                ? ensureProductos()
                : null;
        const categoriesRequest = status.categorias === 'error'
            ? retryCategorias()
            : status.categorias === 'idle'
                ? ensureCategorias()
                : null;

        productsRequest?.catch(() => {});
        categoriesRequest?.catch(() => {});
    }, [
        ensureCategorias,
        ensureProductos,
        isOpen,
        retryCategorias,
        retryProductos,
        status.categorias,
        status.productos,
    ]);

    useEffect(() => {
        setVisibleProductCount(RESULTS_PAGE_SIZE);
    }, [normalizedSearchTerm]);

    const filteredResults = useMemo(() => {
        if (!normalizedSearchTerm) {
            return [];
        }

        return filterCatalogProducts(productos, categorias, searchTerm);
    }, [categorias, normalizedSearchTerm, productos, searchTerm]);
    const categoryNames = useMemo(
        () => createCatalogCategoryNameMap(categorias),
        [categorias]
    );
    const visibleResults = useMemo(
        () => filteredResults.slice(0, visibleProductCount),
        [filteredResults, visibleProductCount]
    );
    const rankedSections = useMemo(
        () => createRankedSections(visibleResults, categoryNames),
        [categoryNames, visibleResults]
    );

    const showResults = Boolean(normalizedSearchTerm);
    const canShowResults = showResults && catalogLoadState === 'loaded';
    const noResults = showResults
        && catalogLoadState === 'loaded'
        && filteredResults.length === 0;

    const handleSearch = (event) => {
        setSearchTerm(event.target.value);
    };

    const handleShowMore = () => {
        setVisibleProductCount(previous => previous + RESULTS_PAGE_SIZE);
    };

    const handleViewAllResults = () => {
        const cleanedSearchTerm = cleanCatalogText(searchTerm);

        if (!cleanedSearchTerm) {
            return;
        }

        const nextParams = new URLSearchParams();
        nextParams.set('buscar', cleanedSearchTerm);
        navigate({
            pathname: '/productos',
            search: `?${nextParams.toString()}`,
        });
        onRequestClose();
    };

    return (
        <Modal
            isOpen={isOpen}
            onAfterOpen={() => inputRef.current?.focus()}
            onAfterClose={onAfterClose}
            onRequestClose={onRequestClose}
            className="modalInput"
            overlayClassName="overlayInput"
            contentLabel="Buscar productos"
            shouldReturnFocusAfterClose
        >
            <div className="public-search">
                <header className="public-search__header">
                    <button
                        type="button"
                        className="public-search__close"
                        onClick={onRequestClose}
                        aria-label="Cerrar búsqueda"
                    >
                        <FontAwesomeIcon icon={faArrowLeft} aria-hidden="true" />
                    </button>
                    <h2>Buscar productos</h2>
                </header>

                <fieldset className="inputSearch">
                    <FontAwesomeIcon icon={faSearch} className="search-icon" aria-hidden="true" />
                    <input
                        ref={inputRef}
                        type="search"
                        placeholder="Buscar..."
                        value={searchTerm}
                        onChange={handleSearch}
                        className="input"
                        aria-label="Buscar productos"
                    />
                </fieldset>

                <div className="modalSearch" aria-live="polite">
                    {canShowResults && rankedSections.map(({
                        categoryKey,
                        categoryName,
                        productos: categoryProducts,
                    }, sectionIndex) => (
                        <section
                            key={`${categoryKey}-${sectionIndex}`}
                            className="sectionSearch"
                        >
                            <h3>{categoryName}</h3>
                            <hr />
                            {categoryProducts.map((producto) => {
                                const productTitle = cleanCatalogText(producto?.titulo)
                                    || 'Producto';

                                return (
                                    <Link
                                        key={producto?.idProducto}
                                        to={`/producto/${producto?.idProducto}/${productTitle.replace(/\s+/g, '-')}`}
                                        onClick={onRequestClose}
                                        aria-label={`Ver producto ${productTitle}`}
                                    >
                                        <img src={producto?.imagen1} alt="" loading="lazy" decoding="async" />
                                        <p>{productTitle}</p>
                                    </Link>
                                );
                            })}
                        </section>
                    ))}
                    {canShowResults && filteredResults.length > visibleProductCount && (
                        <button
                            type="button"
                            onClick={handleShowMore}
                            className="show-more-btn2"
                        >
                            Mostrar más
                        </button>
                    )}
                    {canShowResults && filteredResults.length > 0 && (
                        <button
                            type="button"
                            className="show-more-btn2"
                            onClick={handleViewAllResults}
                        >
                            Ver todos los resultados
                        </button>
                    )}
                    {noResults && <p>No se encontraron resultados.</p>}
                    {catalogLoadState === 'error' && (
                        <p role="alert">
                            No fue posible cargar la búsqueda. Cierra y vuelve a intentarlo.
                        </p>
                    )}
                    {!showResults && catalogLoadState !== 'error' && (
                        <p className="public-search__hint">
                            Escribe el nombre de un producto o categoría.
                        </p>
                    )}
                </div>
            </div>
        </Modal>
    );
}
